// Libraries
import React, { useEffect, useState, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";

// Components
import CategoryTags from "./CategoryTags";
import CandidateSearchAndTags from "../candidate/CandidateSearchAndTags";
import SingleImageUploader from "../shared/imageUploaders/singleImage/SingleImageUploader";
import QuillEditor from "./QuillEditor";
import FactCheck from "./FactCheck";

// Ant Design Components
import { BackTop, Form, Input, message, Select } from "antd";

// Helpers & Config
import NoCapManifest from "../../nocap.json";
import config from "../../config";
import { CandidateContext } from "../../providers/CandidateContext";
import { NewsContext } from "../../providers/NewsContext";
import CreateForm from "../forms/CreateForm";
import DynamicCityStateDistrict from "../forms/candidateForm/components/DynamicCityStateDistrict";

// ---------- localStorage safe helper ----------
const safeJSONParse = (value, fallback = null) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const NewNewsPost = () => {
  const [loading, setLoading] = useState(false);

  // Scroll to top of page when viewed
  useEffect(() => {
    window.scroll({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  // States from context provider
  const { candidateTags, setCandidateTags } = useContext(CandidateContext);
  const { factCheckTags, setFactCheckTags } = useContext(NewsContext);

  useEffect(() => {
    setCandidateTags([]);
    setFactCheckTags([]); // ✅ ensure clean slate for a new post
    // eslint-disable-next-line
  }, []);

  const [currentCategory, setCurrentCategory] = useState("");
  const [candidateData, setCandidateData] = useState({ candidates: [] });

  // Form States
  const [formData, setFormData] = useState({
    title: "",
    blogType: "",
    author: "",
    coverImage: "",
    body: "",
    videoUrl: "",
    editorial: "",
    state: "",
    district: "",
  });

  // Ant Design Title Form Field
  const [form] = Form.useForm();

  // Navigation and Buttons
  const history = useHistory();

  const handleCandidateSearch = async (search) => {
    try {
      const result = await axios.get(
        `${config.CANDIDATES}/candidates/search?search-text=${encodeURIComponent(
          search
        )}&limit=100`
      );
      setCandidateData(result.data);
    } catch (e) {
      console.log(e);
      message.error("Something went wrong when searching candidates.");
    }
  };

  const handleClearCandidateSearch = () => {
    setCandidateData({ candidates: [] });
  };

  const handleCreateNewsPost = async () => {
    setLoading(true);

    const reducedTags = (candidateTags || [])
      .map((tag) => tag?._id)
      .filter(Boolean);

    const newsPost = {
      ...formData,
      // ✅ if user typed directly in form fields, prefer form values
      title: form.getFieldValue("title") ?? formData.title,
      author: form.getFieldValue("author") ?? formData.author,
      editorial: form.getFieldValue("editorial") ?? formData.editorial,
      blogType: form.getFieldValue("blogType") ?? formData.blogType,

      category: currentCategory,
      candidates: reducedTags,
      state: form.getFieldValue("state") || "",
      district: form.getFieldValue("district") || "",
      factCheck: factCheckTags || [],
    };

    try {
      await axios.post(`${config.BLOGS}/blogs/new/`, newsPost, {
        headers: {
          token: localStorage.getItem("access_token"),
        },
      });

      message.success("Your blog post has been saved successfully!");
      message.warning("Redirecting...", 1);
      history.goBack();
    } catch (err) {
      console.log(err);
      message.warning("Your blog post wasn't saved‼️");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Role-based render (safe localStorage parse)
  const user = useMemo(
    () => safeJSONParse(localStorage.getItem("user"), {}) || {},
    []
  );

  // ✅ Avoid returning `message.warning(...)` as render output (can cause flaky blank screens)
  if (user?.role !== "admin") {
    message.warning("Please sign in as an administrator to create a new article.");
    return <div style={{ padding: 24 }} />;
  }

  return (
    <Form
      name="newsPostForm"
      form={form}
      layout="vertical"
      initialValues={{
        title: "",
        author: "",
        editorial: "",
        district: "",
        state: "",
        blogType: "",
      }}
      onFinish={handleCreateNewsPost}
    >
      <CreateForm submitLabel="Create Article" loading={loading}>
        <CategoryTags
          categories={NoCapManifest.news.newsCategories}
          width="95%"
          value={currentCategory}
          onChange={setCurrentCategory}
        />

        <SingleImageUploader
          handleImageUpload={(url) =>
            setFormData((prev) => ({ ...prev, coverImage: url }))
          }
          imagePreviewUrl={formData.coverImage}
          typeKey={"newsCover"}
          imageOptional={false}
          layout={"newsCover"}
        />

        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: "Title is required!" }]}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, title: e.target.value }))
          }
        >
          <Input />
        </Form.Item>

        <Form.Item label="Paid Blog" name="blogType">
          <Select
            showSearch
            placeholder="select your blog type"
            onChange={(value) =>
              setFormData((prev) => ({ ...prev, blogType: value }))
            }
            options={[
              { value: "yes", label: "yes" },
              { value: "no", label: "no" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="author"
          label="Author"
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, author: e.target.value }))
          }
        >
          <Input />
        </Form.Item>

        <Form.Item>
          <DynamicCityStateDistrict form={form} />
        </Form.Item>

        <QuillEditor
          value={formData.body}
          onChange={(html) =>
            setFormData((prev) => ({ ...prev, body: html }))
          }
        />

        <FactCheck />

        <Form.Item
          name="editorial"
          label="Editorial/NoCap Commentary"
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, editorial: e.target.value }))
          }
        >
          <Input.TextArea />
        </Form.Item>

        <CandidateSearchAndTags
          candidateData={candidateData}
          onSearch={(value) => handleCandidateSearch(value)}
          onClick={handleClearCandidateSearch}
          loading={loading}
        />
      </CreateForm>

      <BackTop />
    </Form>
  );
};

export default NewNewsPost;
